import { useCallback, useEffect, useMemo, useState } from 'react'

import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { setStyle } from 'Pods/ThreeDStudio/Store/actions/renderSettings'
import { closeStylePanel } from 'Pods/ThreeDStudio/Store/actions/ui'

import { STYLE_TAG_ALL } from './constants'
import useStylePresets from './Hooks/useStylePresets'

export default function useStylePanel() {
  const { styles, isLoading, isError } = useStylePresets()
  const selectedStyleId = useThreeDStudioStore(state => state.renderSettings.style?.id)
  const [query, setQuery] = useState('')
  const [activeTagId, setActiveTagId] = useState(STYLE_TAG_ALL)
  // Snapshotted at mount - the panel only mounts while open, so this is the style that
  // was selected when it opened. Pinning that id (rather than reading the live
  // selection) keeps the grid order stable for as long as the panel is up.
  const [pinnedStyleId] = useState(selectedStyleId)

  const visibleStyles = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase()

    const matches = styles.filter((style) => {
      const matchesTag = activeTagId === STYLE_TAG_ALL || style.tags.includes(activeTagId)
      const matchesQuery = !normalizedQuery || style.name.toLowerCase().includes(normalizedQuery)

      return matchesTag && matchesQuery
    })

    // The selected style leads the grid so it is visible without scrolling. Skipped
    // when nothing is selected, or when the current tag/search filtered it out.
    const pinnedIndex = matches.findIndex(style => style.id === pinnedStyleId)
    if (pinnedIndex <= 0) {
      return matches
    }

    return [
      matches[pinnedIndex],
      ...matches.slice(0, pinnedIndex),
      ...matches.slice(pinnedIndex + 1),
    ]
  }, [styles, query, activeTagId, pinnedStyleId])

  // The panel is a non-modal dialog rendered next to the field that opened it, so it
  // has no focus trap; Escape is the only keyboard way out.
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeStylePanel()
      }
    }
    document.addEventListener('keydown', handleKeyDown)

    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  const handleQueryChange = useCallback((event) => {
    setQuery(event.target.value)
  }, [])

  // Re-picking the selected style clears it back to "None", so the field can be
  // reset without a separate control.
  const handleSelectStyle = useCallback((style) => {
    setStyle(style.id === selectedStyleId ? null : style)
    closeStylePanel()
  }, [selectedStyleId])

  return {
    query,
    activeTagId,
    visibleStyles,
    selectedStyleId,
    isLoading,
    isError,
    handleQueryChange,
    handleSelectTag: setActiveTagId,
    handleSelectStyle,
  }
}
